import {Component, OnInit, ViewChild} from '@angular/core';
import {TableSelectionAbstract} from '../../../../shared/component/table/table-selection.abstract';
import {DxDataGridComponent} from 'devextreme-angular';
import {AppConfigService} from '../../../../../app-config.service';
import {GeneralService} from '../../../../service/general-service';
import {OrderService} from '../../../../service/order-service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '../../../../service/notification.service';
import {DateFormatPipe} from '../../../../shared/pipe/format-date.pipe';
import {Constant} from '../../../../shared/constants/constant.class';
import {Workbook} from 'exceljs';
import {saveAs} from 'file-saver-es';
import CustomStore from 'devextreme/data/custom_store';
import {LoadOptions} from 'devextreme/data';
import {lastValueFrom} from 'rxjs';

@Component({
  selector: 'app-zalo-log',
  templateUrl: './zalo-log.component.html',
  styleUrls: ['./zalo-log.component.scss']
})
export class ZaloLogComponent extends TableSelectionAbstract implements OnInit {
  // dataSource giờ là CustomStore để dx-data-grid tự gọi API mỗi khi đổi trang/số dòng
  // (server-side paging), thay vì tải hết dữ liệu về rồi phân trang client như trước.
  dataSource: any;
  data: any;
  loading: boolean;
  page: any;
  pageSizeOption: any;
  defaultPageSize: any;

  dateFormat = 'dd/MM/yyyy';
  filter: any;
  currentFilter: any;
  partners = [];
  cachedPayload: any;

  // dữ liệu đang xem chi tiết request/response
  selectedLog: any = null;
  isVisibleDetail = false;

  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  constructor(
    private configService: AppConfigService,
    private generalService: GeneralService,
    private orderService: OrderService,
    public translate: TranslateService,
    private notificationService: NotificationService,
    private dateFormatPipe: DateFormatPipe,
  ) {
    super('id');
    this.filter = {
      sid: null,
      partnerIds: [],
      fromDate: new Date(),
      toDate: new Date(),
    };
    this.getPartners();
  }

  getPartners() {
    this.generalService.getListPartner().subscribe((res) => {
      if (res !== null) {
        this.partners = res;
      }
    });
  }

  ngOnInit(): void {
    this.pageSizeOption = this.configService.getConfig().pageSize;
    this.page = this.configService.getConfig().page;
    this.defaultPageSize = this.configService.getConfig().defaultPage;
    this.initTableHeight(1200);
    this.loadGrid();
  }

  getRowIndex = (index, pageIndex, pageSize) => index + 1 + pageSize * (pageIndex - 1);

  // Khởi tạo CustomStore: mỗi lần dx-data-grid cần dữ liệu (mở trang, đổi trang, đổi số
  // dòng/trang) nó sẽ tự gọi load() bên dưới, tính pageNumber/pageSize theo skip/take
  // rồi gọi API ZaloLogFilter đúng payload BE yêu cầu (pageNumber, pageSize, fromDate,
  // toDate, sid, partnerIds). filter được giữ theo tham chiếu (không gán lại) nên khi
  // người dùng bấm "Tìm kiếm" chỉ cần refresh/đổi trang, không cần tạo lại CustomStore.
  loadGrid() {
    const orderService = this.orderService;
    const filter = this.filter;
    const thisCom = this;
    const defaultPageSize = this.defaultPageSize || 20;

    this.dataSource = new CustomStore({
      key: 'id',
      load: async (loadOptions: LoadOptions) => {
        try {
          const skip = loadOptions.skip ?? 0;
          const take = loadOptions.take ?? defaultPageSize;
          const pageNumber = Math.floor(skip / take) + 1;

          const payload = {
            pageNumber,
            pageSize: take,
            fromDate: filter.fromDate,
            toDate: filter.toDate,
            sid: filter.sid,
            partnerIds: filter.partnerIds && filter.partnerIds.length ? filter.partnerIds : [],
          };
          thisCom.cachedPayload = payload;

          const res = await lastValueFrom(orderService.getZaloLogFilter(payload));
          const items = res?.data || [];
          thisCom.transformData(items);

          return {
            data: items,
            totalCount: res?.total || 0
          };
        } catch (err) {
          console.log(err);
          return {data: [], totalCount: 0};
        }
      }
    });
  }

  // Parse zaloResponse (chuỗi JSON) để lấy trạng thái gửi tin: thành công/thất bại + lý do lỗi
  transformData(list: any[]) {
    list.forEach((item) => {
      item.statusText = 'Không xác định';
      item.statusSuccess = false;
      item.errorMessage = '';
      try {
        const response = item.zaloResponse ? JSON.parse(item.zaloResponse) : null;
        const result = response ? response.Result : null;
        if (result) {
          if (result.error === 0) {
            item.statusText = 'Thành công';
            item.statusSuccess = true;
          } else {
            item.statusText = 'Thất bại';
            item.statusSuccess = false;
            item.errorMessage = result.message;
          }
        }
      } catch (e) {
        item.statusText = 'Không xác định';
      }
    });
  }

  // Bấm "Tìm kiếm": đưa grid về trang 1 để load lại theo filter mới nhất.
  // Lưu ý: nếu grid đang SẴN ở trang 1 (pageIndex = 0) thì gọi pageIndex(0) là no-op,
  // DevExtreme sẽ không bắn request nào cả (không có event trên Network tab) -> phải
  // check trước, nếu đã ở trang 0 rồi thì gọi refresh() để ép load lại.
  doSearch() {
    if (!this.dataGrid) {
      this.loadGrid();
      return;
    }
    const grid = this.dataGrid.instance;
    if (grid.pageIndex() === 0) {
      grid.refresh();
    } else {
      grid.pageIndex(0);
    }
  }

  clearSearch() {
    this.filter.sid = null;
    this.filter.partnerIds = [];
    this.filter.fromDate = new Date();
    this.filter.toDate = new Date();
    this.doSearch();
  }

  get() {
    this.translate.use(this.translate.currentLang).subscribe((data) => {
      this.data = data;
    });
  }

  showDetail(item: any) {
    this.selectedLog = item;
    this.isVisibleDetail = true;
  }

  closeDetail() {
    this.isVisibleDetail = false;
    this.selectedLog = null;
  }

  // format lại chuỗi JSON cho dễ đọc trong popup chi tiết
  prettyJson(jsonStr: string): string {
    if (!jsonStr) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch (e) {
      return jsonStr;
    }
  }

  // Xuất Excel: vì bảng giờ phân trang server (chỉ giữ 1 trang dữ liệu trong grid),
  // không thể xuất trực tiếp từ dxDataGrid như trước nữa. Gọi lại API với cùng điều
  // kiện lọc hiện tại (SID, CTV, khoảng ngày) nhưng lấy pageSize lớn để có đủ toàn bộ
  // dữ liệu khớp filter rồi tự dựng file Excel.
  exportData() {
    const payload = {
      pageNumber: 1,
      pageSize: 20000,
      fromDate: this.filter.fromDate,
      toDate: this.filter.toDate,
      sid: this.filter.sid,
      partnerIds: this.filter.partnerIds && this.filter.partnerIds.length ? this.filter.partnerIds : [],
    };
    this.loading = true;
    this.orderService.getZaloLogFilter(payload).subscribe((res) => {
      this.loading = false;
      const list = res?.data || [];
      this.transformData(list);
      this.buildExcel(list);
    }, () => {
      this.loading = false;
      this.notificationService.showNotification(Constant.ERROR, Constant.MESSAGE_SERVICE_ERROR);
    });
  }

  buildExcel(list: any[]) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('ZaloLog');
    worksheet.columns = [
      {header: 'Ngày gửi', key: 'dateSend', width: 20},
      {header: 'SID', key: 'orderCode', width: 15},
      {header: 'SĐT', key: 'phoneNo', width: 15},
      {header: 'Người gửi', key: 'userSend', width: 15},
      {header: 'Loại gửi', key: 'autoSendText', width: 12},
      {header: 'Trạng thái', key: 'statusText', width: 12},
      {header: 'Lý do lỗi', key: 'errorMessage', width: 35},
    ];
    list.forEach((item) => {
      worksheet.addRow({
        dateSend: item.dateSend ? this.dateFormatPipe.transformFull(item.dateSend, Constant.DATE_FMT_HOUR) : '',
        orderCode: item.orderCode,
        phoneNo: item.phoneNo,
        userSend: item.userSend,
        autoSendText: item.autoSend ? 'Tự động' : 'Thủ công',
        statusText: item.statusText,
        errorMessage: item.errorMessage
      });
    });

    const from = this.dateFormatPipe.transformFull(this.filter.fromDate, Constant.DATE_FMT_STR);
    const to = this.dateFormatPipe.transformFull(this.filter.toDate, Constant.DATE_FMT_STR);
    const dateStr = from === to ? from : (from + '_' + to);
    const fileName = `Zalo_log_${dateStr}`;
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], {type: 'application/octet-stream'}), fileName + '.xlsx');
    });
  }
}
